/**
 * Firestore implementation of ClaimRepository.
 *
 * Path: claims/{id}
 *
 * Staff claims — out-of-pocket reimbursements. See src/types/claim.ts.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Claim, EntityId } from '../../../types';
import type { ClaimRepository } from '../types';

const COLLECTION = 'claims';

export const firestoreClaimRepository: ClaimRepository = {
  async findById(id: EntityId): Promise<Claim | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as Claim) : null;
  },

  async save(entity: Claim): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<Claim[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as Claim);
  },

  async findByOwner(ownerEmail: string): Promise<Claim[]> {
    const q = query(collection(db, COLLECTION), where('ownerEmail', '==', ownerEmail));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Claim);
  },

  async findByStatus(status: Claim['status']): Promise<Claim[]> {
    const q = query(collection(db, COLLECTION), where('status', '==', status));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Claim);
  },
};
